import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ClockCircleOutlined, DownOutlined, TrophyOutlined } from "@ant-design/icons";
import pordefecto from "../pordefecto.png";
import { FranjaFija } from "./franjaFija";

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const VISIBLES_INICIAL = 5;

// Para buscar sin importar mayúsculas ni tildes
const normalizar = (texto) =>
  (texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const formatoFecha = (fecha) => (fecha ? fecha.format("D/M/YYYY") : "A confirmar");

// Sección de calendario: carreras del campeonato seleccionado, con filtros de estado, mes y búsqueda
export const CalendarioAsociacion = ({
  carreras = [],
  categoria,
  campeonato,
  idAsociacion,
  claveReinicio, // cuando cambia (otra categoría o campeonato) se limpian los filtros
  tieneResultados,
  onVerResultados,
}) => {
  const [vista, setVista] = useState(null); // null = automática (próximas si hay, si no todas)
  const [mes, setMes] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    setVista(null);
    setMes("");
    setBusqueda("");
    setExpandido(false);
  }, [claveReinicio]);

  const conFechas = useMemo(
    () =>
      carreras.map((carrera) => ({
        carrera,
        fechaDia: carrera.dia ? moment(carrera.dia) : null,
        fechaPrueba: carrera.dia_prueba ? moment(carrera.dia_prueba) : null,
      })),
    [carreras]
  );

  const hoy = moment().startOf("day");
  const esProxima = (item) => item.fechaDia && item.fechaDia.isSameOrAfter(hoy);
  const hayProximas = conFechas.some(esProxima);
  const vistaActual = vista ?? (hayProximas ? "proximas" : "todas");

  // carreras según el estado elegido; de ahí salen los meses disponibles
  const porVista = conFechas.filter((item) => {
    if (vistaActual === "proximas") return esProxima(item);
    if (vistaActual === "anteriores") return item.fechaDia && !esProxima(item);
    return true;
  });
  const mesesDisponibles = [
    ...new Set(porVista.filter((i) => i.fechaDia).map((i) => i.fechaDia.month())),
  ].sort((a, b) => a - b);

  const texto = normalizar(busqueda.trim());
  const lista = porVista
    .filter((item) => mes === "" || (item.fechaDia && item.fechaDia.month() === Number(mes)))
    .filter((item) => {
      if (!texto) return true;
      const { carrera, fechaDia, fechaPrueba } = item;
      return normalizar(
        [
          carrera.circuito?.nombre,
          carrera.circuito?.localidad,
          formatoFecha(fechaDia),
          formatoFecha(fechaPrueba),
          fechaDia ? MESES[fechaDia.month()] : "",
        ].join(" ")
      ).includes(texto);
    })
    .sort((a, b) => {
      const ta = a.fechaDia ? a.fechaDia.valueOf() : 0;
      const tb = b.fechaDia ? b.fechaDia.valueOf() : 0;
      return vistaActual === "anteriores" ? tb - ta : ta - tb;
    });

  const visibles = expandido ? lista : lista.slice(0, VISIBLES_INICIAL);

  const cambiarVista = (valor) => {
    setVista(valor);
    setMes("");
    setExpandido(false);
  };

  return (
    <FranjaFija izq="#D9D9D9">
    <section className="calendario-asoc" id="calendarios">
      <div className="calendario-asoc-interior">
        {/* la bandera a cuadros hace de contenedor del título y de los filtros */}
        <div className="calendario-asoc-encabezado">
          <div className="calendario-asoc-cabecera">
            <span className="calendario-asoc-barra" />
            <h2 className="calendario-asoc-titulo">Calendario</h2>
            <p className="calendario-asoc-subtitulo">
              <span>{categoria}</span> - {campeonato}
            </p>
          </div>

          {carreras.length > 0 && (
            <div className="calendario-asoc-filtros">
              <label className="calendario-asoc-select">
                <select value={vistaActual} onChange={(e) => cambiarVista(e.target.value)} aria-label="Mostrar carreras">
                  <option value="proximas">Próximas carreras</option>
                  <option value="anteriores">Carreras pasadas</option>
                  <option value="todas">Todas las carreras</option>
                </select>
                <DownOutlined />
              </label>
              <label className="calendario-asoc-select">
                <select
                  value={mes}
                  onChange={(e) => {
                    setMes(e.target.value);
                    setExpandido(false);
                  }}
                  aria-label="Mes"
                >
                  <option value="">Todos los meses</option>
                  {mesesDisponibles.map((m) => (
                    <option key={m} value={m}>
                      {MESES[m]}
                    </option>
                  ))}
                </select>
                <DownOutlined />
              </label>
              <input
                className="calendario-asoc-buscar"
                type="search"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setExpandido(false);
                }}
                placeholder="Buscar Carrera:"
                aria-label="Buscar carrera"
              />
            </div>
          )}
        </div>

        {carreras.length === 0 ? (
          <p className="calendario-asoc-vacio">No hay fechas disponibles.</p>
        ) : (
          <>            {lista.length === 0 ? (
              <p className="calendario-asoc-vacio">No hay carreras para ese filtro.</p>
            ) : (
              <>
                <div className="calendario-asoc-lista">
                  {visibles.map(({ carrera, fechaDia, fechaPrueba }) => {
                    const imagen = carrera.circuito?.imagen
                      ? `${process.env.REACT_APP_DOMINIO_ARCHIVOS}/${idAsociacion}/IMAGENES/CIRCUITOS/${carrera.circuito.imagen}`
                      : pordefecto;
                    return (
                      <article className="calendario-asoc-card" key={carrera.id} translate="no">
                        <img src={imagen} alt={carrera.circuito?.nombre || "Circuito"} />
                        <div className="calendario-asoc-texto">
                          <h3>{carrera.circuito?.localidad || "Localidad no disponible"}</h3>
                          <p>{carrera.circuito?.nombre || "Circuito no disponible"}</p>
                        </div>
                        <span className="calendario-asoc-divisor" />
                        <div className="calendario-asoc-fechas">
                          <div className="calendario-asoc-fechas-lista">
                            <div className="calendario-asoc-fecha">
                              <span className="calendario-asoc-raya" />
                              <div>
                                <span>Pruebas</span>
                                <span>{formatoFecha(fechaPrueba)}</span>
                              </div>
                            </div>
                            <div className="calendario-asoc-fecha">
                              <span className="calendario-asoc-raya" />
                              <div>
                                <span>Carreras</span>
                                <span>{formatoFecha(fechaDia)}</span>
                              </div>
                            </div>
                          </div>
                          {tieneResultados && tieneResultados(carrera) ? (
                            <button
                              type="button"
                              className="calendario-asoc-chip calendario-asoc-chip--resultados"
                              onClick={() => onVerResultados && onVerResultados(carrera)}
                            >
                              <TrophyOutlined />
                              <span>Ver resultados</span>
                            </button>
                          ) : (
                            <span className="calendario-asoc-chip calendario-asoc-chip--pendiente">
                              <ClockCircleOutlined />
                              <span>Resultados pendientes</span>
                            </span>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>

                {lista.length > VISIBLES_INICIAL && (
                  <button type="button" className="calendario-asoc-mas" onClick={() => setExpandido(!expandido)}>
                    {expandido ? "Ver menos" : "Ver calendario completo"}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
    </FranjaFija>
  );
};
